// Seleciona o período letivo "atual" considerando a data do sistema.
// Regras de prioridade:
//   1) Período cuja vigência (data_inicio..data_fim) contém hoje
//   2) Próximo período futuro mais próximo
//   3) Período mais recente já encerrado (fallback)
export interface PeriodoLetivoMin {
  id: string;
  data_inicio?: string | null;
  data_fim?: string | null;
}

export type MotivoSelecaoPeriodo = 'vigente' | 'proximo' | 'encerrado' | 'indefinido';

export interface ResultadoSelecaoPeriodo<T> {
  periodo: T;
  motivo: MotivoSelecaoPeriodo;
}

export function selecionarPeriodoAtual<T extends PeriodoLetivoMin>(
  periodos: T[] | null | undefined,
  hoje: Date = new Date(),
): T | null {
  return selecionarPeriodoAtualDetalhado(periodos, hoje)?.periodo ?? null;
}

export function selecionarPeriodoAtualDetalhado<T extends PeriodoLetivoMin>(
  periodos: T[] | null | undefined,
  hoje: Date = new Date(),
): ResultadoSelecaoPeriodo<T> | null {
  if (!periodos || periodos.length === 0) return null;

  const hojeISO = hoje.toISOString().slice(0, 10);

  // 1) Vigente
  const vigente = periodos.find(p => {
    const ini = p.data_inicio ?? null;
    const fim = p.data_fim ?? null;
    if (!ini) return false;
    if (ini > hojeISO) return false;
    if (fim && fim < hojeISO) return false;
    return true;
  });
  if (vigente) return { periodo: vigente, motivo: 'vigente' };

  // 2) Próximo futuro
  const futuros = periodos
    .filter(p => p.data_inicio && p.data_inicio > hojeISO)
    .sort((a, b) => (a.data_inicio! < b.data_inicio! ? -1 : 1));
  if (futuros.length > 0) return { periodo: futuros[0], motivo: 'proximo' };

  // 3) Mais recente encerrado
  const passados = periodos
    .filter(p => p.data_fim && p.data_fim < hojeISO)
    .sort((a, b) => (a.data_fim! > b.data_fim! ? -1 : 1));
  if (passados.length > 0) return { periodo: passados[0], motivo: 'encerrado' };

  return { periodo: periodos[0], motivo: 'indefinido' };
}

/**
 * Determina o motivo a exibir para o período atualmente selecionado em relação a hoje.
 */
export function classificarPeriodoSelecionado<T extends PeriodoLetivoMin>(
  periodo: T | null | undefined,
  hoje: Date = new Date(),
): MotivoSelecaoPeriodo | null {
  if (!periodo) return null;
  const hojeISO = hoje.toISOString().slice(0, 10);
  const ini = periodo.data_inicio ?? null;
  const fim = periodo.data_fim ?? null;
  if (ini && ini <= hojeISO && (!fim || fim >= hojeISO)) return 'vigente';
  if (ini && ini > hojeISO) return 'proximo';
  if (fim && fim < hojeISO) return 'encerrado';
  return 'indefinido';
}
